import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import integrationService from '../../services/integrationService'
import { getErrorMessage } from '../../utils/errorHandlers'

const EVENT_TYPES = [
  'ORDER_PLACED', 'ORDER_CONFIRMED', 'ORDER_PROCESSING', 'ORDER_PACKED',
  'ORDER_SHIPPED', 'ORDER_DELIVERED', 'ORDER_CANCELLED',
  'PAYMENT_RECEIVED', 'PAYMENT_FAILED', 'INVOICE_GENERATED', 'INVOICE_PAID',
  'ESTIMATE_SENT', 'PURCHASE_ORDER_SENT',
  'DELIVERY_CHALLAN_DISPATCHED', 'DELIVERY_CHALLAN_DELIVERED',
  'SHIPMENT_UPDATE', 'STOCK_ALERT', 'CUSTOMER_WELCOME',
  'PASSWORD_RESET', 'OTP_VERIFICATION', 'CUSTOM',
]

interface EmailTemplateRow {
  id: string
  name: string
  code: string
  event_type: string
  subject?: string
  content?: string
  html_content?: string
  is_active: boolean
  variables: string[]
}

interface TemplateFormState {
  name: string
  code: string
  event_type: string
  subject: string
  content: string
  html_content: string
  variables: string
  is_active: boolean
}

const emptyForm: TemplateFormState = {
  name: '',
  code: '',
  event_type: 'CUSTOM',
  subject: '',
  content: '',
  html_content: '',
  variables: '',
  is_active: true,
}

const EmailTemplatesTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [templates, setTemplates] = useState<EmailTemplateRow[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<TemplateFormState>(emptyForm)
  const [saving, setSaving] = useState(false)

  const loadTemplates = async () => {
    setLoading(true)
    try {
      const data = await integrationService.getTemplates({ channel: 'email' })
      setTemplates(data as unknown as EmailTemplateRow[])
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load templates'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const openCreateDialog = () => {
    setEditingId(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEditDialog = (template: EmailTemplateRow) => {
    setEditingId(template.id)
    setForm({
      name: template.name,
      code: template.code,
      event_type: template.event_type,
      subject: template.subject || '',
      content: template.content || '',
      html_content: template.html_content || '',
      variables: (template.variables || []).join(', '),
      is_active: template.is_active,
    })
    setDialogOpen(true)
  }

  const closeDialog = () => setDialogOpen(false)

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim() || !form.subject.trim() || !form.content.trim()) return
    setSaving(true)
    const variables = form.variables
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)

    try {
      if (editingId) {
        await integrationService.updateTemplate(editingId, {
          name: form.name,
          subject: form.subject,
          content: form.content,
          html_content: form.html_content || undefined,
          variables,
          is_active: form.is_active,
        } as any)
        enqueueSnackbar('Template updated', { variant: 'success' })
      } else {
        await integrationService.createTemplate({
          name: form.name,
          code: form.code,
          event_type: form.event_type,
          channel: 'EMAIL',
          subject: form.subject,
          content: form.content,
          html_content: form.html_content || undefined,
          variables,
        } as any)
        enqueueSnackbar('Template created', { variant: 'success' })
      }
      closeDialog()
      loadTemplates()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to save template'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (template: EmailTemplateRow) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return
    try {
      await integrationService.deleteTemplate(template.id)
      enqueueSnackbar('Template deleted', { variant: 'success' })
      loadTemplates()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to delete template'), { variant: 'error' })
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Email templates, one per event type. Use full multi-channel template editing on the{' '}
          <a href="/admin/integrations/templates">Templates</a> page if you need WhatsApp/SMS variants too.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Create Template
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No email templates yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={template.event_type} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={template.is_active ? 'Active' : 'Inactive'}
                      color={template.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(template)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(template)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Template' : 'Create Email Template'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            {!editingId && (
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                helperText="Unique identifier, e.g. estimate_sent_email"
                fullWidth
                required
              />
            )}
            {!editingId && (
              <TextField
                select
                label="Event Type"
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                fullWidth
              >
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              helperText="Use {{variable_name}} placeholders"
              fullWidth
              required
            />
            <TextField
              label="Plain-text Content"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              multiline
              minRows={4}
              helperText="Use {{variable_name}} placeholders"
              fullWidth
              required
            />
            <TextField
              label="HTML Content"
              value={form.html_content}
              onChange={(e) => setForm({ ...form, html_content: e.target.value })}
              multiline
              minRows={4}
              helperText="Optional — HTML version of the email body"
              fullWidth
            />
            <TextField
              label="Variables"
              value={form.variables}
              onChange={(e) => setForm({ ...form, variables: e.target.value })}
              helperText="Comma-separated, e.g. customer_name, order_number"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmailTemplatesTab
